import { Link } from 'react-router-dom'
import { Phone, MapPin, Mail } from 'lucide-react'
import { useTranslation } from "react-i18next";
import { SITE_EMAIL } from '@/config/site'

export const Footer = () => {
    const { t } = useTranslation();
    return (
        <footer className="relative z-40 bg-[#0a0a0a] text-white pt-16 pb-8 overflow-hidden min-h-[400px]">
            {/* Liquid Glass Background Elements */}
            <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <div
                className="absolute inset-0 opacity-[0.04] pointer-events-none bg-[radial-gradient(circle,rgba(255,255,255,0.35)_1px,transparent_1px)] bg-[length:20px_20px]"
                aria-hidden="true"
            />
            <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/20 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-5 gap-12 mb-12">
                    {/* Brand */}
                    <div className="space-y-6">
                        <Link to="/" className="block" aria-label="Sushi King - Strona główna">
                            <img loading="lazy" src="/images/logo.avif" alt="Sushi King Logo" className="h-16 w-auto" width="75" height="64" />
                        </Link>
                        <p className="text-gray-400 text-sm leading-relaxed max-w-xs">
                            {t('najlepszeSushiWMiescieLczymy')}</p>
                    </div>

                    {/* Links */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-bold font-display tracking-wide text-white">{t('menu')}</h3>
                        <ul className="space-y-3">
                            <li><Link to="/menu" className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary/50"></span>{t('pelneMenu')}</Link></li>
                            <li><Link to="/kreator" className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary/50"></span>{t('kreatorZestawu')}</Link></li>
                            <li><Link to="/akademia" className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary/50"></span>{t('akademiaSushiKing')}</Link></li>
                        </ul>
                    </div>

                    {/* Help */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-bold font-display tracking-wide text-white">{t('pomoc')}</h3>
                        <ul className="space-y-3">
                            <li><Link to="/faq" className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary/50"></span>{t('faq')}</Link></li>
                            <li><Link to="/kontakt" className="text-gray-400 hover:text-primary transition-colors flex items-center gap-2"><span className="w-1 h-1 rounded-full bg-primary/50"></span>{t('kontakt')}</Link></li>
                        </ul>
                    </div>

                    {/* Contact */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-bold font-display tracking-wide text-white">{t('kontakt')}</h3>
                        <ul className="space-y-4 text-gray-400">
                            <li className="flex items-start gap-3 group">
                                <div className="p-2 rounded-lg bg-white/5 group-hover:bg-primary/20 transition-colors">
                                    <MapPin className="h-4 w-4 text-primary" />
                                </div>
                                <span className="text-sm group-hover:text-white transition-colors">{t('ulSlska18')}<br />{t('44206Rybnik')}</span>
                            </li>
                            <li className="flex items-center gap-3 group">
                                <div className="p-2 rounded-lg bg-white/5 group-hover:bg-primary/20 transition-colors">
                                    <Phone className="h-4 w-4 text-primary" />
                                </div>
                                <a href="tel:882717529" className="text-sm group-hover:text-white transition-colors">882 717 529</a>
                            </li>
                            <li className="flex items-center gap-3 group">
                                <div className="p-2 rounded-lg bg-white/5 group-hover:bg-primary/20 transition-colors">
                                    <Mail className="h-4 w-4 text-primary" />
                                </div>
                                <a href={`mailto:${SITE_EMAIL}`} className="text-sm group-hover:text-white transition-colors">{t('kontaktsushikingpl')}</a>
                            </li>
                        </ul>
                    </div>

                    {/* Socials */}
                    <div className="space-y-6">
                        <h3 className="text-lg font-bold font-display tracking-wide text-white">{t('obserwujNas')}</h3>
                        <div className="flex gap-4">
                            <a
                                href="https://www.facebook.com/SushiKingRybnik/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl border border-white/10 hover:border-primary/50 hover:bg-primary/20 transition-all duration-300"
                            >
                                <img loading="lazy" src="/SM_icon/Ikona Facebook 2023.svg" alt="Facebook" className="w-6 h-6 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" width="24" height="24" />
                            </a>
                            <a
                                href="https://www.instagram.com/sushi_king_rybnik/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl border border-white/10 hover:border-primary/50 hover:bg-primary/20 transition-all duration-300"
                            >
                                <img loading="lazy" src="/SM_icon/Logo Instagram 2022.svg" alt="Instagram" className="w-6 h-6 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" width="24" height="24" />
                            </a>
                            <a
                                href="https://www.youtube.com/@SushiKingRybnik"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl border border-white/10 hover:border-primary/50 hover:bg-primary/20 transition-all duration-300"
                            >
                                <img loading="lazy" src="/SM_icon/Ikona YouTube 2017.svg" alt="YouTube" className="w-6 h-6 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" width="24" height="24" />
                            </a>
                            <a
                                href="https://www.tiktok.com/@sushi_king_rybnik"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="group relative w-12 h-12 flex items-center justify-center bg-white/5 rounded-xl border border-white/10 hover:border-primary/50 hover:bg-primary/20 transition-all duration-300"
                            >
                                <img loading="lazy" src="/SM_icon/Tiktok Icon White.svg" alt="TikTok" className="w-6 h-6 opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all" width="24" height="24" />
                            </a>
                        </div>
                    </div>
                </div>

                <div className="border-t border-white/10 pt-8 mt-8 flex flex-col md:flex-row justify-center items-center gap-4 text-xs text-gray-500">
                    <p>{t('copy')}{new Date().getFullYear()} {t('sushiKingWszelkiePrawaZastrzezone')}</p>
                </div>
            </div>
        </footer>
    )
}
